import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Typography,
  Chip,
  Tooltip,
  TextField,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import FileUpload from '../../components/Redesing/file-upload';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { getLocaleDateTimeString } from '../../utils';
import {
  DOCUMENTS_QUERY_KEY,
  Document,
  DocumentSortInput,
  fetchDocumentUrl,
  useDocuments,
} from '../../hooks/documents/query/V2/useDocumentsRelay';
import {
  useArchiveDocument,
  useUploadDocument,
} from '../../hooks/documents/mutation/V2/useDocumentMutations';
import { useQueryClient } from '@tanstack/react-query';
import CustomToast from '../../components/Redesing/custom-toast';
import { IAuth } from '../../ts/interface/admins.interface';
import { useSystemStore } from '../../store/systemStore';

// Keep the empty page stable while a request is loading.
const NO_DOCUMENTS: Document[] = [];
const SORT_FIELDS: Record<string, DocumentSortInput['field']> = {
  type: 'CONTENT_TYPE',
  name: 'NAME',
  size: 'BYTE_SIZE',
  uploaded_at: 'INSERTED_AT',
};

const DocumentsTable = ({
  systemLoaded,
  auth,
}: {
  systemLoaded: boolean;
  auth: IAuth;
}) => {
  const queryClient = useQueryClient();
  const systemGid = useSystemStore((state) => state.systemInfo.gid);
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({
    key: 'uploaded_at',
    direction: 'desc' as 'asc' | 'desc',
  });
  const queryContext = JSON.stringify([systemGid, pageSize, search, sort]);
  const [pagination, setPagination] = useState({
    context: queryContext,
    page: 0,
    cursors: [null] as (string | null)[],
  });
  // Never send a cursor from another system, search, sort or page size.
  const pageNo = pagination.context === queryContext ? pagination.page : 0;
  const cursors = pagination.context === queryContext ? pagination.cursors : [null];
  const resetPagination = useCallback(() => {
    setPagination({ context: queryContext, page: 0, cursors: [null] });
  }, [queryContext]);
  useEffect(resetPagination, [resetPagination]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [descriptions, setDescriptions] = useState<{ [key: string]: string }>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  // Fetch documents from ant
  const { data, isLoading, isError, isFetching, refetch } = useDocuments(systemGid, {
    enabled: systemLoaded,
    first: pageSize,
    after: cursors[pageNo],
    search,
    sort: [
      { field: SORT_FIELDS[sort.key], order: sort.direction === 'asc' ? 'ASC' : 'DESC' },
      { field: 'ID', order: 'ASC' },
    ],
  });
  const documents = data?.documents ?? NO_DOCUMENTS;

  const changePage = (nextPage: number) => {
    if (isFetching || isError) return;
    if (
      nextPage === pageNo + 1 &&
      data?.pageInfo.hasNextPage &&
      data.pageInfo.endCursor
    ) {
      setPagination({
        context: queryContext,
        page: nextPage,
        cursors: [...cursors.slice(0, nextPage), data.pageInfo.endCursor],
      });
    } else if (nextPage >= 0 && nextPage < pageNo) {
      setPagination({ context: queryContext, page: nextPage, cursors });
    }
  };

  // Upload document mutation
  const uploadDocumentMutation = useUploadDocument({
    onSuccess: () => {
      resetPagination();
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
    },
    onError: (error) => {
      CustomToast({
        type: 'error',
        message: `Failed to upload document: ${error.message}`,
      });
    },
  });

  // Delete document mutation: ant archives it, keeping the file.
  const deleteDocumentMutation = useArchiveDocument({
    onSuccess: () => {
      resetPagination();
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
      CustomToast({
        type: 'success',
        message: 'Document deleted successfully',
      });
    },
    onError: (error) => {
      CustomToast({
        type: 'error',
        message: `Failed to delete document: ${error.message}`,
      });
    },
  });

  // Handle file drop
  const handleFileDrop = useCallback((acceptedFiles: File[]) => {
    setPendingFiles((prev) => [...prev, ...acceptedFiles]);
    // Initialize descriptions for new files
    const newDescriptions: { [key: string]: string } = {};
    acceptedFiles.forEach((file) => {
      newDescriptions[file.name] = '';
    });
    setDescriptions((prev) => ({ ...prev, ...newDescriptions }));
  }, []);

  // Remove pending file
  const handleRemovePendingFile = useCallback((fileName: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.name !== fileName));
    setDescriptions((prev) => {
      const newDescriptions = { ...prev };
      delete newDescriptions[fileName];
      return newDescriptions;
    });
  }, []);

  // Update description for pending file
  const handleDescriptionChange = useCallback((fileName: string, value: string) => {
    setDescriptions((prev) => ({ ...prev, [fileName]: value }));
  }, []);

  // Add documents from pending files
  const handleAddDocuments = useCallback(async () => {
    const uploads = pendingFiles.map((file) => ({
      file,
      description: descriptions[file.name] || '',
    }));

    try {
      // Upload all files sequentially
      for (const { file, description } of uploads) {
        await uploadDocumentMutation.mutateAsync({
          systemId: systemGid,
          file,
          description,
          category: 'general',
        });
      }

      CustomToast({
        type: 'success',
        message: `Successfully uploaded ${uploads.length} document${
          uploads.length > 1 ? 's' : ''
        }`,
      });

      setPendingFiles([]);
      setDescriptions({});
      setOpenAddModal(false);
    } catch (error) {
      // Error is displayed to the user via mutation onError toast
      // Keep UI state intact so user can retry the upload
      console.error('Upload error:', error);
    }
  }, [pendingFiles, descriptions, uploadDocumentMutation, systemGid]);

  // Delete document
  const handleDeleteDocument = useCallback((docId: string) => {
    setDocumentToDelete(docId);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (documentToDelete) {
      // Close once the request finishes, so the dialog shows "Deleting..." meanwhile
      deleteDocumentMutation.mutate(documentToDelete, {
        onSettled: () => {
          setDeleteDialogOpen(false);
          setDocumentToDelete(null);
        },
      });
    }
  }, [documentToDelete, deleteDocumentMutation]);

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }, []);

  // Open the tab during the click so the later URL refresh isn't popup-blocked.
  const handleViewDocument = useCallback(async (doc: Document) => {
    if (!doc.url) return;
    const tab = window.open('', '_blank');
    if (!tab) {
      CustomToast({ type: 'error', message: 'Allow pop-ups to view this document.' });
      return;
    }
    // Keep a handle for navigation while isolating the destination from this app.
    tab.opener = null;
    tab.document.title = 'Opening document…';
    const referrer = tab.document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = 'no-referrer';
    tab.document.head.appendChild(referrer);

    try {
      const url = await fetchDocumentUrl(doc.id);
      if (!tab.closed) tab.location.replace(url);
    } catch (error) {
      tab.close();
      CustomToast({
        type: 'error',
        message: `Failed to open document: ${
          error instanceof Error ? error.message : 'Please try again.'
        }`,
      });
    }
  }, []);

  // Download document
  const handleDownloadDocument = useCallback(async (doc: Document) => {
    if (!doc.url) return;
    try {
      const downloadUrl = await fetchDocumentUrl(doc.id);
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Failed to download document: ${
          error instanceof Error ? error.message : 'Please try again.'
        }`,
      });
    }
  }, []);

  // Close modals
  const handleCloseAddModal = useCallback(() => {
    setOpenAddModal(false);
    setPendingFiles([]);
    setDescriptions({});
  }, []);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (Math.round((bytes / Math.pow(k, i)) * 100) / 100).toFixed(2) + ' ' + sizes[i];
  };

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        id: 'type',
        key: 'type',
        label: 'Type',
        sortable: true,
        format: (doc: Document) => (
          <Chip
            icon={doc.type === 'pdf' ? <PictureAsPdfIcon /> : <ImageIcon />}
            label={doc.type.toUpperCase()}
            size="small"
            color={doc.type === 'pdf' ? 'error' : 'primary'}
            variant="outlined"
          />
        ),
      },
      {
        id: 'name',
        key: 'name',
        label: 'Document Name',
        sortable: true,
        format: (doc: Document) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {doc.name}
            </Typography>
            {doc.description && (
              <Typography variant="caption" color="text.secondary">
                {doc.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: 'size',
        key: 'size',
        label: 'Size',
        sortable: true,
        format: (doc: Document) => (
          <Typography variant="body2">
            {doc.size === null ? '—' : formatFileSize(doc.size)}
          </Typography>
        ),
      },
      {
        id: 'uploaded_at',
        key: 'uploaded_at',
        label: 'Uploaded',
        sortable: true,
        format: (doc: Document) => (
          <Typography variant="body2">
            {getLocaleDateTimeString(doc.uploaded_at)}
          </Typography>
        ),
      },
      {
        id: 'actions',
        key: 'actions',
        label: 'Actions',
        notSortable: true,
        disableCLick: true,
        format: (doc: Document) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="View document">
              <IconButton
                size="small"
                disabled={!doc.url}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDocument(doc);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download document">
              <IconButton
                size="small"
                disabled={!doc.url}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadDocument(doc);
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete document">
              <IconButton
                size="small"
                disabled={!auth.admin.movaticAccess}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteDocument(doc.id);
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [
      handleViewDocument,
      handleDownloadDocument,
      handleDeleteDocument,
      auth.admin.movaticAccess,
    ]
  );

  return (
    <>
      <Card>
        {isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" disabled={isFetching} onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Could not load documents. Please try again.
          </Alert>
        ) : (
          <PaginationTable
            isLoading={isLoading || isFetching}
            customKey={'id'}
            localSearch={false}
            dataId="statements-documents"
            padding={{ pt: 1, pl: 0, pr: 1 }}
            sortBy={sort.key}
            sortDirection={sort.direction}
            onSortChange={({ key, direction }) => {
              if (SORT_FIELDS[key])
                setSort({ key, direction: direction === 'asc' ? 'asc' : 'desc' });
            }}
            items={documents}
            totalItems={data?.count ?? 0}
            hasMore={!isFetching && data?.pageInfo.hasNextPage}
            noDataSlice
            noDataText={search ? 'No matching documents.' : 'No Documents Added Yet!'}
            columns={columns}
            rowsPerPage={pageSize}
            page={pageNo}
            onPageChange={(page, pageN: number) => {
              changePage(pageN);
            }}
            onRowsPerPageChange={(rowsPerPage) =>
              setPageSize(Number(rowsPerPage.target.value))
            }
            onRowCLick={(id: string) => {
              const doc = documents.find((item) => item.id === id);
              if (doc) handleViewDocument(doc);
            }}
            searchText={search}
            searchPlaceholder="Search documents"
            handleSearch={(search) => setSearch(search.target.value)}
            buttons={[
              {
                label: 'Add Document',
                startIcon: <AddIcon />,
                onClick: () => setOpenAddModal(true),
                disabled: !auth.admin.movaticAccess,
              },
            ]}
          />
        )}
      </Card>

      {/* Add Document Modal */}
      <Dialog open={openAddModal} onClose={handleCloseAddModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Add Documents</Typography>
            <IconButton size="small" onClick={handleCloseAddModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FileUpload
              onDrop={handleFileDrop}
              caption="Upload PDFs or images (PNG, JPG, JPEG). Max 10MB per file."
              accept="application/pdf,image/png,image/jpeg,image/jpg"
              maxSize={10000000}
            />

            {pendingFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Pending Files ({pendingFiles.length})
                </Typography>
                <Stack spacing={2}>
                  {pendingFiles.map((file) => {
                    const isImage = file.type.startsWith('image/');
                    const isPdf = file.type === 'application/pdf';
                    return (
                      <Card key={file.name} variant="outlined">
                        <Box p={2}>
                          <Stack spacing={2}>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                flex={1}
                              >
                                {isPdf ? (
                                  <PictureAsPdfIcon color="error" fontSize="large" />
                                ) : isImage ? (
                                  <ImageIcon color="primary" fontSize="large" />
                                ) : (
                                  <DescriptionIcon color="action" fontSize="large" />
                                )}
                                <Box flex={1}>
                                  <Typography variant="body2" fontWeight={500}>
                                    {file.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(file.size)}
                                  </Typography>
                                </Box>
                              </Stack>
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePendingFile(file.name)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                            <TextField
                              size="small"
                              label="Description (optional)"
                              placeholder="Add a description for this document..."
                              value={descriptions[file.name] || ''}
                              onChange={(e) =>
                                handleDescriptionChange(file.name, e.target.value)
                              }
                              fullWidth
                              multiline
                              rows={2}
                            />
                          </Stack>
                        </Box>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddModal}
            disabled={uploadDocumentMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddDocuments}
            disabled={
              pendingFiles.length === 0 ||
              uploadDocumentMutation.isPending ||
              !auth.admin.movaticAccess
            }
            startIcon={
              uploadDocumentMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
          >
            {uploadDocumentMutation.isPending
              ? 'Uploading...'
              : `Add ${pendingFiles.length} ${
                  pendingFiles.length === 1 ? 'Document' : 'Documents'
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={cancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deleteDocumentMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteDocumentMutation.isPending}
            startIcon={
              deleteDocumentMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteDocumentMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default connect(
  (state: {
    system: { isLoaded: boolean };
    admin: {
      admin: IAuth;
    };
  }) => ({
    systemLoaded: state.system.isLoaded,
    auth: state.admin.admin,
  }),
  () => ({})
)(DocumentsTable);
